import React, { useCallback, useMemo } from "react";
import { Box, Flex, Text, Badge, Avatar, Dialog } from "@radix-ui/themes";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import { Minus } from "@geist-ui/icons";
import { format } from "date-fns";
import { useQuery } from "react-query";
import styles from "./styles.module.scss";
import { useReleaseApi } from "@/v2/entities/release/Release.api";

interface ConversationHistoryProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conversationId: string;
  releaseId: string;
}

export function ConversationHistory({
  open,
  onOpenChange,
  conversationId,
  releaseId,
}: ConversationHistoryProps) {
  const { getReleaseConversations } = useReleaseApi();

  const {
    data: releaseConversations,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["release-conversation", releaseId],
    queryFn: () => getReleaseConversations(releaseId, true),
    enabled: !!conversationId && open,
  });

  const conversation = useMemo(() => {
    return releaseConversations?.find(
      (conv) => conv.id?.toString() === conversationId
    );
  }, [releaseConversations, conversationId]);

  const filteredComments = useMemo(() => {
    return conversation?.releaseComments?.filter(
      (comment) =>
        !comment.comment.toLowerCase().includes("reset") &&
        !comment.comment.includes("--")
    );
  }, [conversation?.releaseComments]);

  const formatMessage = useCallback((message: string) => {
    if (message === "talk_to_an_agent") {
      return "Quack will escalate it to an agent";
    }
    return message;
  }, []);

  if (!conversationId || !open) return null;

  if (isLoading) {
    return (
      <Dialog.Root open={open} onOpenChange={onOpenChange}>
        <Dialog.Content className={styles.drawerContainer}>
          <Dialog.Title>
            <Text size="5" weight="bold">
              Session History
            </Text>
          </Dialog.Title>
          <Box className={styles.messagesContainer}>
            <Text size="2" color="gray">
              Loading conversation...
            </Text>
          </Box>
        </Dialog.Content>
      </Dialog.Root>
    );
  }

  if (error || !conversation) {
    return (
      <Dialog.Root open={open} onOpenChange={onOpenChange}>
        <Dialog.Content className={styles.drawerContainer}>
          <Dialog.Title>
            <Text size="5" weight="bold">
              Session History
            </Text>
          </Dialog.Title>
          <Box className={styles.messagesContainer}>
            <Text size="2" color="red">
              Error loading conversation
            </Text>
          </Box>
        </Dialog.Content>
      </Dialog.Root>
    );
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className={styles.drawerContainer}>
        <Dialog.Title>
          <Text size="5" weight="bold">
            Session History
          </Text>
        </Dialog.Title>

        <Box className={styles.messagesContainer}>
          {filteredComments?.map((comment, index: number) => (
            <Flex
              key={index}
              direction="column"
              className={`${styles.messageWrapper} ${
                comment.isAgent ? styles.agent : styles.user
              }`}
            >
              <Flex
                align="center"
                gap="2"
                className={
                  comment.isAgent ? styles.agentLabel : styles.userLabel
                }
              >
                <Box className={styles.avatarWrapper}>
                  <Avatar
                    size="2"
                    fallback={comment.isAgent ? "Q" : "U"}
                    className={
                      comment.isAgent ? styles.agentAvatar : styles.userAvatar
                    }
                    radius="full"
                  />
                </Box>
                <Text size="2" weight="medium">
                  {comment.isAgent ? "Quack" : "User"}
                </Text>
              </Flex>
              <Box
                className={
                  comment.isAgent ? styles.agentMessage : styles.userMessage
                }
              >
                <Text
                  size="2"
                  align="left"
                  className={
                    comment.isAgent ? styles.agentText : styles.userText
                  }
                >
                  {formatMessage(comment.comment)}
                </Text>
              </Box>
            </Flex>
          ))}

          <div className={styles.activityLog}>
            <div className={styles.activityItem}>
              <Badge
                size="1"
                color={
                  conversation.feedback === "POSITIVE"
                    ? "green"
                    : conversation.feedback === "NEGATIVE"
                      ? "red"
                      : "gray"
                }
                className={styles.feedbackBadge}
              >
                {conversation.feedback === "POSITIVE" ? (
                  <ThumbsUp size={12} />
                ) : conversation.feedback === "NEGATIVE" ? (
                  <ThumbsDown size={12} />
                ) : (
                  <Minus size={12} />
                )}
              </Badge>
              <Text size="2" color="gray">
                {conversation.feedback === "POSITIVE"
                  ? "Good"
                  : conversation.feedback === "NEGATIVE"
                    ? "Bad"
                    : "No feedback"}{" "}
                • {format(new Date(conversation.createdAt), "MMM d, h:mm a")}
              </Text>
            </div>
          </div>
        </Box>
      </Dialog.Content>
    </Dialog.Root>
  );
}
